import {
  BlockRenderer,
  interceptPreviewPageLinkClick,
  resolveEditTargetFromElement,
  SiteCookieSettingsBar,
} from '@web-studio/core-components';
import {
  buildLocalizedSitePath,
  buildProjectThemeCssProperties,
  compositionSiteDataAttributes,
  hydrateLayoutBlocksWithPersona,
  publicPathForPageSlug,
  LAYOUT_COMPONENT_TYPE,
  isGlobalFloatComponentType,
  isGlobalTopComponentType,
  resolveFooterLegalLinks,
  resolveNavGround,
  type CornerEnumKey,
  type CustomColorSet,
  type LayoutBlock,
  type SpacingEnumKey,
  type TypographyEnumKey,
} from '@web-studio/shared-schemas';
import type { CSSProperties, MouseEvent } from 'react';
import { useSandboxPreviewStore } from '@/previewStore';
import { postBlockSelectToShell } from '@/utils/postBlockSelect';
import { postLocaleSelectToShell } from '@/utils/postLocaleSelect';
import { postPageSelectToShell } from '@/utils/postPageSelect';
import type { PageMeta, SiteMode } from '@web-studio/shared-schemas';
import { useCallback, useEffect, useMemo } from 'react';
import { useScrollToActiveBlock } from '@/hooks/useScrollToActiveBlock';

function useThemeStyle(
  themeId: string,
  colorsetId: string,
  spacingEnum: SpacingEnumKey,
  cornerEnum: CornerEnumKey,
  typographyEnum: TypographyEnumKey,
  customColorSets: CustomColorSet[],
): CSSProperties | undefined {
  const vars = buildProjectThemeCssProperties(themeId, colorsetId, spacingEnum, {
    cornerEnum,
    typographyEnum,
    customColorSets,
  });
  return vars ? (vars as CSSProperties) : undefined;
}

interface PreviewSegmentProps {
  blocks: LayoutBlock[];
  locale: string;
  activeBlockId: string | null;
  activeEditTarget: ReturnType<typeof useSandboxPreviewStore.getState>['activeEditTarget'];
  editable: boolean;
  allBlocks: LayoutBlock[];
  pages: PageMeta[];
  siteMode?: SiteMode;
  vertical?: string;
  defaultLocale?: string;
  supportedLocales?: string[];
  themeId?: string;
  colorsetId?: string;
  hasTastingTimeline?: boolean;
  pathname?: string | null;
  onNavigateToPageSlug?: (slug: string) => void;
  onLocaleChange?: (locale: string) => void;
}

function PreviewSegment({
  blocks,
  locale,
  activeBlockId,
  activeEditTarget,
  editable,
  allBlocks,
  pages,
  siteMode,
  vertical,
  defaultLocale,
  supportedLocales,
  themeId,
  colorsetId,
  hasTastingTimeline,
  pathname,
  onNavigateToPageSlug,
  onLocaleChange,
}: PreviewSegmentProps) {
  const ordered = [...blocks].sort((a, b) => a.displayOrder - b.displayOrder);

  return (
    <>
      {ordered.map((block) => (
        <BlockRenderer
          key={block.blockId}
          block={block}
          locale={locale}
          activeBlockId={activeBlockId}
          activeEditTarget={activeEditTarget}
          editable={editable}
          allBlocks={allBlocks}
          pages={pages}
          defaultLocale={defaultLocale}
          supportedLocales={supportedLocales}
          siteMode={siteMode}
          vertical={vertical}
          themeId={themeId}
          colorsetId={colorsetId}
          hasTastingTimeline={hasTastingTimeline}
          pathname={pathname}
          onNavigateToPageSlug={onNavigateToPageSlug}
          onLocaleChange={onLocaleChange}
        />
      ))}
    </>
  );
}

export function PreviewCanvas() {
  const locale = useSandboxPreviewStore((s) => s.locale);
  const interactionMode = useSandboxPreviewStore((s) => s.interactionMode);
  const activeBlockId = useSandboxPreviewStore((s) => s.activeBlockId);
  const activeEditTarget = useSandboxPreviewStore((s) => s.activeEditTarget);
  const blocks = useSandboxPreviewStore((s) => s.blocks);
  const globalBlocks = useSandboxPreviewStore((s) => s.globalBlocks);
  const canvasBlocks = useSandboxPreviewStore((s) => s.canvasBlocks);
  const activePageId = useSandboxPreviewStore((s) => s.activePageId);
  const pages = useSandboxPreviewStore((s) => s.pages);
  const siteMode = useSandboxPreviewStore((s) => s.siteMode);
  const vertical = useSandboxPreviewStore((s) => s.vertical);
  const showInvisibleBlocks = useSandboxPreviewStore((s) => s.showInvisibleBlocks);
  const defaultLocale = useSandboxPreviewStore((s) => s.defaultLocale);
  const supportedLocales = useSandboxPreviewStore((s) => s.supportedLocales);
  const scopedPreview = useSandboxPreviewStore((s) => s.scopedPreview);
  const themeId = useSandboxPreviewStore((s) => s.themeId);
  const hasTastingTimeline = useSandboxPreviewStore((s) => s.hasTastingTimeline);
  const colorsetId = useSandboxPreviewStore((s) => s.colorsetId);
  const customColorSets = useSandboxPreviewStore((s) => s.customColorSets);
  const spacingEnum = useSandboxPreviewStore((s) => s.spacingEnum);
  const cornerEnum = useSandboxPreviewStore((s) => s.cornerEnum);
  const typographyEnum = useSandboxPreviewStore((s) => s.typographyEnum);
  const personaBranding = useSandboxPreviewStore((s) => s.personaBranding);
  const businessName = useSandboxPreviewStore((s) => s.businessName);
  /** Nested social URLs must bust hydrate memo when only Persona.socialLinks changes. */
  const personaSocialKey = JSON.stringify(personaBranding?.socialLinks ?? null);

  const themeStyle = useThemeStyle(
    themeId,
    colorsetId,
    spacingEnum,
    cornerEnum,
    typographyEnum,
    customColorSets,
  );
  const editable = interactionMode === 'edit';

  const hydrateLocales = useMemo(
    () =>
      [...new Set([locale, defaultLocale, ...supportedLocales, 'de'].filter(Boolean) as string[])],
    [defaultLocale, locale, supportedLocales],
  );
  const hydrateOpts = useMemo(
    () => ({ businessName, locales: hydrateLocales }),
    [businessName, hydrateLocales],
  );

  // Render-time hydrate: keeps Imprint/footer bindings in sync with Persona even
  // when an earlier BLOCKS_SYNC arrived before branding was available.
  const displayGlobalBlocks = useMemo(
    () => hydrateLayoutBlocksWithPersona(globalBlocks, personaBranding, hydrateOpts),
    [globalBlocks, hydrateOpts, personaBranding, personaSocialKey],
  );
  const displayCanvasBlocks = useMemo(
    () => hydrateLayoutBlocksWithPersona(canvasBlocks, personaBranding, hydrateOpts),
    [canvasBlocks, hydrateOpts, personaBranding, personaSocialKey],
  );
  const displayBlocks = useMemo(
    () => hydrateLayoutBlocksWithPersona(blocks, personaBranding, hydrateOpts),
    [blocks, hydrateOpts, personaBranding, personaSocialKey],
  );
  const visibleGlobalBlocks = useMemo(
    () =>
      showInvisibleBlocks
        ? displayGlobalBlocks
        : displayGlobalBlocks.filter((block) => block.hidden !== true),
    [displayGlobalBlocks, showInvisibleBlocks],
  );
  const visibleCanvasBlocks = useMemo(
    () =>
      showInvisibleBlocks
        ? displayCanvasBlocks
        : displayCanvasBlocks.filter((block) => block.hidden !== true),
    [displayCanvasBlocks, showInvisibleBlocks],
  );
  const visibleBlocks = useMemo(
    () =>
      showInvisibleBlocks ? displayBlocks : displayBlocks.filter((block) => block.hidden !== true),
    [displayBlocks, showInvisibleBlocks],
  );

  useEffect(() => {
    const root = document.documentElement;
    root.setAttribute('data-theme', themeId);
    if (colorsetId) {
      root.setAttribute('data-colorset', colorsetId);
    } else {
      root.removeAttribute('data-colorset');
    }
  }, [themeId, colorsetId]);

  const canvasRevision = useMemo(
    () =>
      [
        activePageId ?? '',
        personaBranding?.legalName ?? '',
        personaSocialKey,
        ...displayCanvasBlocks.map((block) => block.blockId),
        ...displayGlobalBlocks.map((block) => block.blockId),
      ].join(':'),
    [activePageId, displayCanvasBlocks, displayGlobalBlocks, personaBranding?.legalName, personaSocialKey],
  );
  useScrollToActiveBlock(activeBlockId, activePageId, canvasRevision);

  useEffect(() => {
    if (!activePageId) {
      return;
    }
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [activePageId]);

  const onBlockSelect = editable ? postBlockSelectToShell : undefined;
  const handleNavigateToPageSlug = useCallback(
    (slug: string) => {
      const page = pages.find((entry) => entry.slug === slug);
      if (page) {
        postPageSelectToShell(page.pageId, page.slug);
      }
    },
    [pages],
  );
  const browseNavigate =
    !editable && pages.length > 0 ? handleNavigateToPageSlug : undefined;
  const handleLocaleChange = useCallback((nextLocale: string) => {
    postLocaleSelectToShell(nextLocale);
  }, []);
  const browseLocaleChange = !editable ? handleLocaleChange : undefined;
  const activePageSlug = pages.find((entry) => entry.pageId === activePageId)?.slug;
  const pathname = activePageSlug
    ? buildLocalizedSitePath(
        publicPathForPageSlug(activePageSlug),
        locale,
        defaultLocale || 'de',
      )
    : null;

  const privacyHref = useMemo(() => {
    const links = resolveFooterLegalLinks(pages, locale, defaultLocale || 'de');
    return links.find((link) => link.slug === 'datenschutz')?.href ?? '/datenschutz';
  }, [defaultLocale, locale, pages]);

  const handleClickCapture = (event: MouseEvent<HTMLDivElement>) => {
    if (browseNavigate) {
      interceptPreviewPageLinkClick(event, pages, browseNavigate, {
        supportedLocales,
        defaultLocale: defaultLocale || 'de',
        onLocaleChange: browseLocaleChange,
      });
      return;
    }

    if (!editable || !onBlockSelect) {
      return;
    }

    const editTarget = resolveEditTargetFromElement(event.target as HTMLElement);
    if (!editTarget) {
      return;
    }

    const blockEl = (event.target as HTMLElement).closest('[data-block-id]');
    const blockId = blockEl?.getAttribute('data-block-id');
    if (!blockId) {
      return;
    }

    event.preventDefault();
    onBlockSelect(blockId, editTarget);
  };

  const handleClick = (event: MouseEvent<HTMLDivElement>) => {
    if (!editable || !onBlockSelect) {
      return;
    }

    if (resolveEditTargetFromElement(event.target as HTMLElement)) {
      return;
    }

    const blockEl = (event.target as HTMLElement).closest('[data-block-id]');
    const blockId = blockEl?.getAttribute('data-block-id');
    if (!blockId) {
      return;
    }

    onBlockSelect(blockId);
  };

  if (displayBlocks.length === 0) {
    const embedded = typeof window !== 'undefined' && window.parent !== window;
    return (
      <div className="flex min-h-screen items-center justify-center bg-neutral-100 p-8">
        <div className="max-w-md space-y-2 text-center">
          <p className="text-sm text-neutral-600">
            {embedded
              ? 'Warte auf Website-Daten aus dem Studio…'
              : 'Diese Vorschau läuft nur eingebettet im Studio.'}
          </p>
          <p className="text-xs text-neutral-500">
            {embedded
              ? 'Wenn das hängen bleibt: Studio unter https://studio.huba.info öffnen und die Seite neu laden.'
              : 'Öffne ein Projekt unter https://studio.huba.info — die Vorschau erscheint im Studio-Canvas.'}
          </p>
        </div>
      </div>
    );
  }

  const topBlocks = scopedPreview
    ? visibleGlobalBlocks
        .filter((block) => isGlobalTopComponentType(block.componentType))
        .sort((a, b) => a.displayOrder - b.displayOrder)
    : [];
  const navBlocks = scopedPreview
    ? visibleGlobalBlocks.filter(
        (block) => block.componentType === LAYOUT_COMPONENT_TYPE.navigationHeader,
      )
    : [];
  const footerBlocks = scopedPreview
    ? visibleGlobalBlocks.filter(
        (block) => block.componentType === LAYOUT_COMPONENT_TYPE.footerStandard,
      )
    : [];
  const floatBlocks = scopedPreview
    ? visibleGlobalBlocks
        .filter((block) => isGlobalFloatComponentType(block.componentType))
        .sort((a, b) => a.displayOrder - b.displayOrder)
    : [];

  // Nav chrome harmony must see this page's hero only — not a leftover home Full Bleed.
  const pageAllBlocks = scopedPreview
    ? [...topBlocks, ...navBlocks, ...visibleCanvasBlocks, ...footerBlocks, ...floatBlocks]
    : visibleBlocks;
  /** Adaptive Overlay: hero ground only when THIS page has a cinematic underlay. */
  const navGround = resolveNavGround(pageAllBlocks);
  /** Same composition chrome as SitePage / edge SSR (section-align, brand-in-hero, atmosphere). */
  const compositionAttrs = compositionSiteDataAttributes(themeId);

  const segmentProps = {
    locale,
    activeBlockId,
    activeEditTarget,
    editable,
    allBlocks: pageAllBlocks,
    pages,
    siteMode,
    vertical,
    defaultLocale,
    supportedLocales,
    themeId,
    colorsetId,
    hasTastingTimeline,
    pathname,
    onNavigateToPageSlug: browseNavigate,
    onLocaleChange: browseLocaleChange,
  };

  return (
    <div
      className={['ws-site', editable ? 'ws-site--editable' : ''].filter(Boolean).join(' ')}
      data-theme={themeId}
      data-colorset={colorsetId}
      data-nav-ground={navGround}
      {...compositionAttrs}
      {...(themeStyle ? { style: themeStyle } : {})}
      id="ws-site-root"
      onClickCapture={editable || browseNavigate ? handleClickCapture : undefined}
      onClick={editable ? handleClick : undefined}
      role={editable ? 'presentation' : undefined}
    >
      {scopedPreview ? (
        <>
          <PreviewSegment blocks={topBlocks} {...segmentProps} />
          <PreviewSegment blocks={navBlocks} {...segmentProps} />
          <main key={activePageId ?? 'canvas'} className="ws-page-canvas" data-page-id={activePageId ?? undefined}>
            <PreviewSegment blocks={visibleCanvasBlocks} {...segmentProps} />
          </main>
          <PreviewSegment blocks={footerBlocks} {...segmentProps} />
          <PreviewSegment blocks={floatBlocks} {...segmentProps} />
        </>
      ) : (
        <PreviewSegment blocks={visibleBlocks} {...segmentProps} />
      )}
      <SiteCookieSettingsBar
        locale={locale}
        privacyHref={privacyHref}
        suppressAutoOpen={editable}
      />
    </div>
  );
}
